import {InjectionKey, type Ref, ref} from "vue";
import axios from "axios";
import type {User} from "@/types";

// Define an InjectionKey for the user context
export const userKey: InjectionKey<{ user: Ref<User | undefined>; fetchUser: () => Promise<void> }> = Symbol("user");

/**
 * Provides user state, and a function to fetch the user from the API.
 *
 * @param {User} [initialUser] - Optional initial user data to set.
 * @returns {Object} - The user state, and a fetch function.
 *   - `user`: The user data, or undefined if the user is not loaded.
 *   - `fetchUser`: The function to fetch the user data from the API.
 */
export function useUser(initialUser?: User): { user: Ref<User | undefined>; fetchUser: () => Promise<void> } {
    const user = ref<User | undefined>(initialUser);

    /**
     * Fetch user data from the API and update the user state.
     */
    const fetchUser = async () => {
        try {
            const response = await axios.get("/auth/user");
            user.value = response.data.user;
        } catch {
            user.value = undefined;
        }
    };

    return {user, fetchUser};
}
